import { supabase } from './supabase';
import { BankAccount, BankAccountType } from '../types/database';

export interface CreateBankAccountData {
  company_id: string;
  nickname: string;
  bank_name: string;
  account_number: string;
  branch_number?: string | null;
  routing_number?: string | null;
  currency: string;
  is_active?: boolean;
  account_type?: BankAccountType;
  linked_checking_account_id?: string | null;
  card_provider?: string | null;
}

export interface UpdateBankAccountData {
  nickname?: string;
  bank_name?: string;
  account_number?: string;
  branch_number?: string | null;
  routing_number?: string | null;
  currency?: string;
  is_active?: boolean;
  account_type?: BankAccountType;
  linked_checking_account_id?: string | null;
  card_provider?: string | null;
}

export const bankAccountsService = {
  /**
   * Get all bank accounts for a company
   */
  async getBankAccounts(companyId: string) {
    try {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Get bank accounts error:', error);
        return { data: null, error };
      }

      return { data: data as BankAccount[], error: null };
    } catch (err) {
      console.error('Get bank accounts exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Get a single bank account by ID
   */
  async getBankAccount(accountId: string) {
    try {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (error) {
        console.error('Get bank account error:', error);
        return { data: null, error };
      }

      return { data: data as BankAccount, error: null };
    } catch (err) {
      console.error('Get bank account exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Create a new bank account
   */
  async createBankAccount(accountData: CreateBankAccountData) {
    try {
      const { data, error } = await supabase
        .from('bank_accounts')
        .insert({
          company_id: accountData.company_id,
          nickname: accountData.nickname,
          bank_name: accountData.bank_name,
          account_number: accountData.account_number,
          branch_number: accountData.branch_number || null,
          routing_number: accountData.routing_number || null,
          currency: accountData.currency,
          is_active: accountData.is_active ?? true,
          account_type: accountData.account_type || 'checking',
          linked_checking_account_id: accountData.linked_checking_account_id || null,
          card_provider: accountData.card_provider || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Create bank account error:', error);
        return { data: null, error };
      }

      return { data: data as BankAccount, error: null };
    } catch (err) {
      console.error('Create bank account exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Update a bank account
   */
  async updateBankAccount(accountId: string, updates: UpdateBankAccountData) {
    try {
      const { data, error } = await supabase
        .from('bank_accounts')
        .update(updates)
        .eq('id', accountId)
        .select()
        .single();

      if (error) {
        console.error('Update bank account error:', error);
        return { data: null, error };
      }

      return { data: data as BankAccount, error: null };
    } catch (err) {
      console.error('Update bank account exception:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Delete a bank account
   */
  async deleteBankAccount(accountId: string) {
    try {
      const { error } = await supabase
        .from('bank_accounts')
        .delete()
        .eq('id', accountId);

      if (error) {
        console.error('Delete bank account error:', error);
        return { error };
      }

      return { error: null };
    } catch (err) {
      console.error('Delete bank account exception:', err);
      return { error: err };
    }
  },

  /**
   * Toggle active status of a bank account
   */
  async toggleBankAccountStatus(accountId: string, isActive: boolean) {
    try {
      const { data, error } = await supabase
        .from('bank_accounts')
        .update({ is_active: isActive })
        .eq('id', accountId)
        .select()
        .single();

      if (error) {
        console.error('Toggle bank account status error:', error);
        return { data: null, error };
      }

      return { data: data as BankAccount, error: null };
    } catch (err) {
      console.error('Toggle bank account status exception:', err);
      return { data: null, error: err };
    }
  },
};
